// ローカル MediaStream 取得まわりの共通ロジック
// 設計書 §3.4.4 browser_settings の入力値とローカルプレビューを束ねる。

export interface CaptureSettings {
  videoDeviceId?: string;
  audioDeviceId?: string;
  width: number;
  height: number;
  framerate: number;
  bitrateKbps: number;
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
  /** カメラの代わりに画面共有を VDO Ninja の push 経路で送出する。
   *  カメラ未接続時の動作確認や、デモ・チュートリアル配信用途。 */
  useScreenshare: boolean;
}

export const DEFAULT_CAPTURE_SETTINGS: CaptureSettings = {
  width: 1280,
  height: 720,
  framerate: 30,
  bitrateKbps: 2500,
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
  useScreenshare: false,
};

export function buildMediaConstraints(s: CaptureSettings): MediaStreamConstraints {
  return {
    video: {
      ...(s.videoDeviceId ? { deviceId: { exact: s.videoDeviceId } } : {}),
      width: { ideal: s.width },
      height: { ideal: s.height },
      frameRate: { ideal: s.framerate },
    },
    audio: {
      ...(s.audioDeviceId ? { deviceId: { exact: s.audioDeviceId } } : {}),
      echoCancellation: s.echoCancellation,
      noiseSuppression: s.noiseSuppression,
      autoGainControl: s.autoGainControl,
    },
  };
}

export interface MediaError {
  code: string;
  message: string;
}

export function describeMediaError(err: unknown): MediaError {
  if (err instanceof DOMException) {
    switch (err.name) {
      case 'NotAllowedError':
      case 'SecurityError':
        return {
          code: err.name,
          message:
            'カメラ・マイクのアクセスが拒否されました。ブラウザの権限設定で許可してください。',
        };
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        return { code: err.name, message: 'カメラまたはマイクが見つかりません。' };
      case 'NotReadableError':
      case 'TrackStartError':
        return {
          code: err.name,
          message: 'カメラ/マイクが他のアプリで使われています。他を閉じてください。',
        };
      case 'OverconstrainedError':
      case 'ConstraintNotSatisfiedError':
        return {
          code: err.name,
          message: '指定の解像度/フレームレートはこのデバイスでは利用できません。',
        };
      default:
        return { code: err.name, message: err.message || '映像/音声の取得に失敗しました' };
    }
  }
  return { code: 'UnknownError', message: '映像/音声の取得に失敗しました' };
}
